import React, { useState, useEffect } from 'react';
import './DirectoryTree.css';

const STORAGE_KEY = 'sutra-expanded-folders';

function DirectoryTree({ tree, onFileClick }) {
  const [expandedFolders, setExpandedFolders] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return new Set(parsed);
      }
      return new Set();
    } catch {
      return new Set();
    }
  });

  // Persist expanded state
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(expandedFolders)));
    } catch (error) {
      console.error('Failed to save expanded folders:', error);
    }
  }, [expandedFolders]);

  const toggleFolder = (path) => {
    setExpandedFolders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(path)) {
        newSet.delete(path);
      } else {
        newSet.add(path);
      }
      return newSet;
    });
  };

  const handleFileClick = (node) => {
    if (onFileClick && node.slug) {
      onFileClick({
        id: node.slug,
        slug: node.slug,
        title: node.title || node.name,
        path: node.path
      });
    }
  };

  if (!tree) {
    return <div className="tree-loading">Loading...</div>;
  }

  return (
    <div className="directory-tree">
      {tree.children && tree.children.map((child, index) => (
        <TreeNode
          key={child.path || `root-${index}`}
          node={child}
          level={0}
          expandedFolders={expandedFolders}
          onToggle={toggleFolder}
          onFileClick={handleFileClick}
        />
      ))}
    </div>
  );
}

function TreeNode({ node, level, expandedFolders, onToggle, onFileClick }) {
  if (!node) return null;

  const isFolder = node.type === 'folder';
  const isExpanded = expandedFolders.has(node.path);
  const hasChildren = isFolder && node.children && node.children.length > 0;

  const handleClick = () => {
    if (isFolder) {
      onToggle(node.path);
    } else {
      onFileClick(node);
    }
  };

  return (
    <>
      <div
        className={`tree-node ${isFolder ? 'folder' : 'file'} ${isExpanded ? 'expanded' : ''}`}
        style={{
          paddingLeft: `${level * 16 + 8}px`,
          '--level': level
        }}
        onClick={handleClick}
      >
        <span className={`tree-arrow ${!isFolder || !hasChildren ? 'tree-arrow-empty' : ''}`}>
          {isFolder && hasChildren ? '❯' : ''}
        </span>

        <span className="tree-name">
          {node.title || node.name}
        </span>
      </div>

      {isFolder && hasChildren && isExpanded && (
        <div className="tree-children" style={{ '--parent-level': level }}>
          {node.children.map((child, index) => (
            <TreeNode
              key={child.path || `${node.path}-${index}`}
              node={child}
              level={level + 1}
              expandedFolders={expandedFolders}
              onToggle={onToggle}
              onFileClick={onFileClick}
            />
          ))}
        </div>
      )}
    </>
  );
}

export default DirectoryTree;
